import { useCallback, useMemo } from 'react';

import { useIsDesktop } from '@/hooks/useIsDesktop';
import { ResponseTapTourneyData } from '@/types/tda';
import { formatNumberToLocale } from '@/utils';

type ContentType = {
  [key: number]: {
    background: string;
    text: JSX.Element;
  };
};

export const PrizeCoverImage = ({
  prize,
  touneyData,
}: {
  prize: number | undefined;
  touneyData: ResponseTapTourneyData | undefined;
}) => {
  const { isMobile } = useIsDesktop();

  const banner1Style = useCallback(() => {
    const xs = isMobile ? '_xs' : '';
    switch (prize) {
      case 75000:
        return `/assets/images/tap2${xs}.jpg`;
      case 150000:
        return `/assets/images/tap3${xs}.jpg`;
      case 250000:
        return `/assets/images/tap1${xs}.jpg`;
      default:
        return `/assets/images/tap2${xs}.jpg`;
    }
  }, [isMobile, prize]);

  const getSecondaryText = useCallback((): JSX.Element => {
    // Condición 1: si existe ganasteF11 y es true
    if (touneyData?.ganasteF11 === true) {
      return (
        <>
          ¡FELICITACIONES!
          <br />
          SOS EL CAMPEÓN DEL TORNEO
        </>
      );
    }

    // Condición 2: si existe nombreGanadorGeneral y es false
    if (touneyData?.nombreGanadorGeneral && !touneyData?.ganasteF11) {
      return (
        <>
          ¡FELICITACIONES A {touneyData.nombreGanadorGeneral},
          <br />
          EL CAMPEÓN DEL TORNEO!
        </>
      );
    }

    // Texto predeterminado si no se cumple ninguna condición
    return (
      <>
        ¡FELICITACIONES!
        <br />
        SOS EL CAMPEÓN DEL TORNEO
      </>
    );
  }, [touneyData?.ganasteF11, touneyData?.nombreGanadorGeneral]);

  const bannerContent: ContentType = useMemo(() => {
    return {
      0: {
        background: banner1Style(),
        text: (
          <>
            ¡EL GANADOR DEL TORNEO {isMobile && <br />} SE LLEVA $
            {formatNumberToLocale(prize ?? 0)}!
          </>
        ),
      },
      1: {
        background: banner1Style(),
        text: getSecondaryText(),
      },
    };
  }, [banner1Style, getSecondaryText, isMobile, prize]);

  // Determinar qué contenido usar (0 o 1) basado en la existencia de touneyData.ganasteF11
  const contentIndex = touneyData?.nombreGanadorGeneral !== undefined ? 1 : 0;

  return (
    <div
      key={prize}
      className={`flex ${
        isMobile ? 'items-start pt-4' : 'items-center'
      } justify-center w-full ${
        isMobile ? 'h-[110px]' : 'h-[144px]'
      } text-white ${
        isMobile ? 'bg-right-bottom' : 'bg-center'
      } bg-cover bg-no-repeat rounded-lg my-4`}
      style={{
        backgroundImage: `url('${bannerContent[contentIndex].background}')`,
        ...(isMobile ? { backgroundPosition: 'right 70%' } : {}),
      }}
    >
      <div className={`text-left w-full ${isMobile ? 'px-5' : 'pl-[150px]'}`}>
        <h2
          className={`font-bebas uppercase ${
            isMobile
              ? 'text-[20px] leading-[22px] pt-4 text-center w-max'
              : 'text-[38px] leading-relaxed'
          } font-normal`}
        >
          {bannerContent[contentIndex].text}
        </h2>
      </div>
    </div>
  );
};
